import React, { useEffect } from 'react';
import { Box, Container } from '@mui/system';
import { CircularProgress } from '@mui/material';
import {
    PrescriberHeader,
    ModificationDate,
    FlexyTabs,
    TabStructure,
    Toolbar,
    NotFound,
    ModificationDateProps,
    useDumbActionsContext,
} from '@europrocurement/flexy-components';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { useApiRequest } from '@europrocurement/l2d-hooks';
import { PrescripteurApi, useSociete, useTiersService } from '@europrocurement/l2d-domain';
import { useParams } from 'react-router';
import ListeSousClientPrescripteurWrapper from '@europrocurement/l2d-modules/modules/Societes/page/liste/ListeSousClientPrescripteurWrapper';
import {
    useDefaultContextActions,
    DocumentPreviewProvider,
    DocumentPreviewOverlay,
} from '@europrocurement/l2d-modules';
import { getSanitizedId } from '../../../../utils/sanitize';
import { useSelectPrescriber } from '../../../../hooks/prescriberHooks';
import {
    Billing,
    Contacts,
    Activities,
    LegalAdvert,
    Offers,
    Pricing,
    Resume,
    Formalities,
    Invoices,
} from './sections';

import { PRESCRIBER_LIST } from '../../constants/paths';
import { PrescriberActionMenu } from './components/PrescriberActionMenu';
import Folios from './sections/Folios';

const PrescriberDetail: React.FunctionComponent = function () {
    const { request, requestState } = useApiRequest<PrescripteurApi>();
    const { response: prescriber, success, isLoaded, notFound } = requestState;
    const { prescribersApi } = useTiersService();
    const { idPrescripteur: id } = useParams();
    const { backContextActions } = useDefaultContextActions();

    useDumbActionsContext(backContextActions);

    const selectPrescriber = useSelectPrescriber();

    React.useEffect(() => {
        if (id) {
            selectPrescriber(+id);
        }
    }, [id, selectPrescriber]);

    const xIdSociete = useSociete();
    const getPrescriber = () => prescribersApi.getTiersTiersItem({ id: id ?? '', xIdSociete });

    /** Toolbar / Breadcrumb ** LazyBreadcrumb path */
    const breadcrumbPath = [
        {
            icon: {
                icon: ['fasl', 'house'] as IconProp,
                sx: {
                    alignItems: 'center',
                    display: 'flex',
                },
            },
            path: 'Accueil',
            link: '/',
        },
        {
            link: PRESCRIBER_LIST,
            path: 'Prescripteurs',
        },
        '',
    ];

    /** DEV: Used to filter available / WIP tabs */
    const tabsShown = ['resume', 'infos', 'formalities', 'societes', 'invoices', 'folios'];

    const getModificationDateProps = (): ModificationDateProps => {
        const creationDate = prescriber?.dateCreation;
        const updateDate = prescriber?.dateMAJ;
        const isCreationDate = !updateDate && creationDate;

        return {
            date: new Date(isCreationDate ? creationDate : updateDate),
            displayIcon: false,
            isCreationDate,
        };
    };

    const responseWrapper = {
        display: 'flex',
        justifyContent: 'center',
        marginTop: '50px',
    };

    const canRender = prescriber && isLoaded && success;
    const fetchPrescriber = () =>
        request(getPrescriber(), {
            withToaster: false,
        });

    useEffect(() => {
        if (getSanitizedId(id)) {
            fetchPrescriber();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!canRender) {
        if (notFound) {
            return <NotFound />;
        }

        return (
            <Container maxWidth="lg">
                <Box
                    display="flex"
                    justifyContent="center"
                    sx={responseWrapper}
                >
                    {!isLoaded ? <CircularProgress /> : <p>Une erreur est survenue</p>}
                </Box>
            </Container>
        );
    }
    const tabProps = {
        prescriber,
        fetchPrescriber,
    };

    const tabs: Array<TabStructure> = [
        {
            tabName: 'resume',
            tabTitle: 'Synthèse',
            tabPanel: <Resume {...tabProps} />,
        },
        {
            tabName: 'infos',
            tabTitle: 'Information & contacts',
            tabPanel: <Contacts {...tabProps} />,
        },
        {
            tabName: 'legal-advert',
            tabTitle: 'Annonces légales',
            tabPanel: <LegalAdvert {...tabProps} />,
            count: 4,
        },
        {
            tabName: 'formalities',
            tabTitle: 'Formalités',
            tabPanel: <Formalities {...tabProps} />,
        },
        {
            tabName: 'billing',
            tabTitle: 'Comptabilités',
            tabPanel: <Billing {...tabProps} />,
        },
        {
            tabName: 'offers',
            tabTitle: 'Offres',
            tabPanel: <Offers {...tabProps} />,
        },
        {
            tabName: 'pricing',
            tabTitle: 'Grille tarifaire',
            tabPanel: <Pricing {...tabProps} />,
        },
        {
            tabName: 'activities',
            tabTitle: 'Activités',
            tabPanel: <Activities {...tabProps} />,
        },
        {
            tabName: 'societes',
            tabTitle: 'Sociétés',
            tabPanel: <ListeSousClientPrescripteurWrapper showPrescripteur={false} />,
        },
        {
            tabName: 'invoices',
            tabTitle: 'Factures',
            tabPanel: <Invoices prescriberId={prescriber?.id} />,
        },
        {
            tabName: 'folios',
            tabTitle: 'Dossiers',
            tabPanel: <Folios prescriberId={prescriber?.id} />,
        },
    ];

    const hasTimestamp = prescriber?.dateMAJ || prescriber?.dateCreation;
    return (
        // <Container maxWidth="lg">
        <>
            <Toolbar
                path={breadcrumbPath}
                previousPath={PRESCRIBER_LIST}
            >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {hasTimestamp && (
                        <Box sx={{ opacity: 0.5 }}>
                            <ModificationDate {...getModificationDateProps()} />
                        </Box>
                    )}
                    <PrescriberActionMenu {...tabProps} />
                </Box>
            </Toolbar>
            <PrescriberHeader prescriber={prescriber} />
            <DocumentPreviewProvider>
                <FlexyTabs
                    routerParameterName="prescriber-detail"
                    sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '15px' }}
                    tabs={tabs.filter((tab) => tabsShown.includes(tab.tabName))}
                    scrollbar={false}
                />
                <DocumentPreviewOverlay />
            </DocumentPreviewProvider>
        </>
        // </Container>
    );
};

export default PrescriberDetail;
